package fr.manyverse.app.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AddAPhoto
import androidx.compose.material.icons.filled.AddPhotoAlternate
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import fr.manyverse.app.domain.model.ImageData
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.add_image
import manyverse.shared.generated.resources.image_preview
import manyverse.shared.generated.resources.remove_image
import org.jetbrains.compose.resources.stringResource

/**
 * A button for selecting and previewing character images.
 *
 * @param currentImageUrl The URL of the existing image (if any)
 * @param selectedImageData The newly selected image data (if any)
 * @param onPickImage Called when the user wants to pick an image
 * @param onRemoveImage Called when the user wants to remove the image
 * @param modifier Optional modifier
 * @param enabled Whether picking and removing are enabled (e.g. disable during upload)
 */
@Composable
fun ImagePickerButton(
    currentImageUrl: String?,
    selectedImageData: ImageData?,
    onPickImage: () -> Unit,
    onRemoveImage: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    Box(modifier = modifier.size(120.dp)) {
        // Main image or placeholder
        Box(
            modifier = Modifier
                .size(120.dp)
                .clip(CircleShape)
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .clickable(enabled = enabled, onClick = onPickImage),
            contentAlignment = Alignment.Center
        ) {
            when {
                selectedImageData != null -> {
                    // Show preview of newly selected image
                    AsyncImage(
                        model = selectedImageData.bytes,
                        contentDescription = stringResource(Res.string.add_image),
                        contentScale = ContentScale.Crop,
                        modifier = Modifier
                            .size(120.dp)
                            .clip(CircleShape)
                    )
                }

                currentImageUrl != null -> {
                    // Show current image
                    AsyncImage(
                        model = currentImageUrl,
                        contentDescription = stringResource(Res.string.add_image),
                        contentScale = ContentScale.Crop,
                        modifier = Modifier
                            .size(120.dp)
                            .clip(CircleShape)
                    )
                }

                else -> {
                    // Placeholder icon
                    Icon(
                        imageVector = Icons.Filled.AddAPhoto,
                        contentDescription = stringResource(Res.string.add_image),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.size(48.dp)
                    )
                }
            }
        }

        // Remove button (only show when there's an image)
        if (currentImageUrl != null || selectedImageData != null) {
            IconButton(
                onClick = onRemoveImage,
                enabled = enabled,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .offset(x = 8.dp, y = (-8).dp)
                    .size(32.dp),
                colors = IconButtonDefaults.iconButtonColors(
                    containerColor = MaterialTheme.colorScheme.error,
                    contentColor = MaterialTheme.colorScheme.onError
                )
            ) {
                Icon(
                    imageVector = Icons.Filled.Close,
                    contentDescription = stringResource(Res.string.remove_image),
                    modifier = Modifier.size(16.dp)
                )
            }
        }
    }
}

/**
 * Image picker showing a large rectangular preview, or a full-width pick button (labelled
 * [pickLabel]) when nothing is available. Used where the image is content rather than an
 * avatar (e.g. announcements, inventory items).
 *
 * The preview shows the newly picked [selectedImageData] if any, otherwise the existing
 * remote [currentImageUrl]. Tapping the preview re-opens the picker (replace). The remove
 * (×) button only appears for a newly picked image, letting the user revert to the
 * existing one.
 */
@Composable
fun ImagePickerPreview(
    selectedImageData: ImageData?,
    onPickImage: () -> Unit,
    onClearImage: () -> Unit,
    pickLabel: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    currentImageUrl: String? = null
) {
    val previewModel: Any? = selectedImageData?.bytes ?: currentImageUrl
    if (previewModel != null) {
        Box(modifier = modifier.fillMaxWidth()) {
            AsyncImage(
                model = previewModel,
                contentDescription = stringResource(Res.string.image_preview),
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(150.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant)
                    .clickable(enabled = enabled, onClick = onPickImage)
            )
            if (selectedImageData != null) {
                IconButton(
                    onClick = onClearImage,
                    enabled = enabled,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(4.dp)
                        .size(32.dp)
                        .background(
                            color = MaterialTheme.colorScheme.error.copy(alpha = 0.8f),
                            shape = RoundedCornerShape(16.dp)
                        )
                ) {
                    Icon(
                        imageVector = Icons.Filled.Close,
                        contentDescription = stringResource(Res.string.remove_image),
                        tint = MaterialTheme.colorScheme.onError,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
        }
    } else {
        OutlinedButton(
            onClick = onPickImage,
            enabled = enabled,
            modifier = modifier.fillMaxWidth()
        ) {
            Icon(
                imageVector = Icons.Filled.AddPhotoAlternate,
                contentDescription = null,
                modifier = Modifier.size(18.dp)
            )
            Text(
                text = pickLabel,
                modifier = Modifier.padding(start = 8.dp)
            )
        }
    }
}
